/**
 * A TO-DO'S "SHOW ON" DATE AND ITS REMINDERS BEFORE THE DUE DATE (packet
 * CX-2). Bobbie, 2026-09-29, verbatim: "this is confusing parents", then
 * "great add the show on! and then add a 90,60, 30, 15, and 7 day reminder
 * options she should be able to select multiple."
 *
 * Why: Kim hand-made a few comp fee to-dos worded "due in N days" from the
 * day she wrote them. A hand-made to-do shows the moment it is made, so
 * parents read "due in N days" the day Kim wrote it, not the day it became
 * true. A Show on date holds the to-do back until the studio day it should
 * appear; the reminders bring it back on the days she picks.
 *
 * ONE FILE FOR BOTH SIDES. The composer, the To-dos page and the server
 * (netlify/functions/lib/taskWrite.mjs for the write, lib/taskSchedule.mjs
 * for the daily sweep) all import the day math and the "may this reminder be
 * picked" answer from here, so the screen can never offer a choice the server
 * refuses, or the other way round. No Firebase in this file, so a plain node
 * test imports it.
 *
 * STUDIO DAYS, NOT CLOCK TIME. Every day here is a 'YYYY-MM-DD' key in
 * America/Chicago (CLAUDE.md: every time shown to Bobbie is studio time).
 * Day arithmetic runs on the keys as calendar days, standing each one in at
 * noon UTC, so a daylight saving change never moves a reminder by a day.
 *
 * HOW A WAITING TO-DO IS HIDDEN. Until its Show on day, the to-do's people
 * are kept in `showOnAssigneeUids` and its `assigneeUids` is EMPTY. Every
 * family and assignee read in the Hub asks Firestore for tasks "assigned to
 * me" (assigneeUids array-contains my uid), and the rules let a family read a
 * task only when its uid is in assigneeUids, so a waiting to-do never reaches
 * a family phone at all, not merely a filtered one. Staff read every task, so
 * the staff lists drop a waiting to-do too (useAllTasks), and only the person
 * who made it and the Directors see it, on the To-dos page, labeled.
 */

/** The reminder choices, days before the due date, largest first. */
export const TASK_REMINDER_DAYS = [90, 60, 30, 15, 7] as const;

export type TaskReminderDay = (typeof TASK_REMINDER_DAYS)[number];

export const TASK_SCHEDULE_TIME_ZONE = 'America/Chicago';

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar day written 'YYYY-MM-DD'. */
export function isDateKey(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_KEY.test(value)) return false;
  const [y, m, d] = value.split('-').map(Number);
  const probe = new Date(Date.UTC(y, m - 1, d, 12));
  return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

/** The studio day an instant falls on, read in America/Chicago. */
export function studioDateKey(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TASK_SCHEDULE_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function keyToNoonUtc(key: string): number {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d, 12);
}

function noonUtcToKey(ms: number): string {
  const date = new Date(ms);
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Calendar days added to a day key; negative goes back. */
export function addDaysToDateKey(key: string, days: number): string {
  return noonUtcToKey(keyToNoonUtc(key) + days * 86_400_000);
}

/** Whole calendar days from one day key to another; negative when `to` is earlier. */
export function daysBetweenDateKeys(from: string, to: string): number {
  return Math.round((keyToNoonUtc(to) - keyToNoonUtc(from)) / 86_400_000);
}

/** The picked reminder days, each a real choice, once, largest first. */
export function normalizeReminderDays(value: unknown): TaskReminderDay[] {
  if (!Array.isArray(value)) return [];
  const picked = new Set<number>();
  for (const item of value) {
    if (typeof item === 'number' && (TASK_REMINDER_DAYS as readonly number[]).includes(item)) picked.add(item);
  }
  return TASK_REMINDER_DAYS.filter((days) => picked.has(days));
}

/** The studio day a reminder N days before the due day falls on. */
export function reminderDayKey(dueKey: string, days: number): string {
  return addDaysToDateKey(dueKey, -days);
}

/** "Nov 20", with the year only when it is not the year of `todayKey`. */
export function shortDateLabel(key: string, todayKey?: string): string {
  if (!isDateKey(key)) return '';
  const date = new Date(keyToNoonUtc(key));
  const sameYear = !todayKey || !isDateKey(todayKey) || key.slice(0, 4) === todayKey.slice(0, 4);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
}

/** "Friday, November 20" */
export function longDateLabel(key: string): string {
  if (!isDateKey(key)) return '';
  return new Date(keyToNoonUtc(key)).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** "Shows on Nov 20" */
export function showOnLabel(key: string, todayKey?: string): string {
  return `Shows on ${shortDateLabel(key, todayKey)}`;
}

/** "30, 15 and 7 days before the due date" */
export function reminderDaysLabel(days: readonly number[]): string {
  const list = normalizeReminderDays(days);
  if (list.length === 0) return '';
  const words = list.map(String);
  const joined = words.length === 1
    ? words[0]
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
  return `${joined} days before the due date`;
}

/** Days left, in the words a reminder uses on the day it is sent. */
export function daysLeftWords(daysLeft: number): string {
  if (daysLeft <= 0) return 'today';
  if (daysLeft === 1) return 'tomorrow';
  return `in ${daysLeft} days`;
}

/**
 * Why a Show on day cannot be used, or null when it can. It has to be a day
 * AFTER today (a to-do that should show today simply shows now), and on or
 * before the due day when there is one: a to-do must never first appear
 * already past due. The server refuses the same days (lib/taskWrite.mjs).
 */
export function showOnProblem(
  showOnKey: string | null | undefined,
  todayKey: string,
  dueKey?: string | null
): string | null {
  if (!showOnKey) return null;
  if (!isDateKey(showOnKey)) return 'That Show on date does not look right.';
  if (showOnKey <= todayKey) return 'Pick a Show on date after today, or leave it blank to show it now.';
  if (isDateKey(dueKey) && showOnKey > dueKey) {
    return `Pick a Show on date on or before the due date, ${shortDateLabel(dueKey, todayKey)}.`;
  }
  return null;
}

/**
 * Will the server door refuse a schedule from this sign-in? It refuses a
 * dancer sign-in (netlify/functions/taskWrite.mjs: a dancerAccount token or
 * an address on the reserved .invalid dancer domain), which is how a Jr
 * Staff on her dancer sign-in reaches the composer. The screen hides the
 * Show on picker and the Change and Cancel buttons there, so it never
 * offers what would fail; she can use them from her staff sign-in.
 */
export function isTaskScheduleDoorClosed(account: {
  authEmail?: string | null;
  userEmail?: string | null;
  authMode?: string | null;
} | null | undefined): boolean {
  const dancerDomain = /@dancer[.]thestudioldg[.]invalid$/i;
  return dancerDomain.test(account?.authEmail ?? '')
    || dancerDomain.test(account?.userEmail ?? '')
    || account?.authMode === 'server_session_v1';
}

export interface ReminderChoiceState {
  days: TaskReminderDay;
  /** The studio day it would be sent, or '' with no due date. */
  dayKey: string;
  allowed: boolean;
  /** Plain words for a choice that cannot be picked; null when it can. */
  reason: string | null;
}

/**
 * Each reminder choice and whether it may be picked. A reminder is sent on
 * its own studio day, so its day has to come after today AND after the Show
 * on day: the day a to-do first appears already sends its own alert, and a
 * reminder before that day would remind people of a to-do they cannot see.
 *
 * The SAME answer decides the checkboxes on screen and what the server
 * accepts (lib/taskWrite.mjs), so a box shown disabled is also refused.
 */
export function reminderChoiceStates(input: {
  dueKey: string | null | undefined;
  todayKey: string;
  showOnKey?: string | null;
}): ReminderChoiceState[] {
  const { todayKey } = input;
  const dueKey = isDateKey(input.dueKey) ? input.dueKey : null;
  const showOnKey = isDateKey(input.showOnKey) ? input.showOnKey : null;
  return TASK_REMINDER_DAYS.map((days) => {
    if (!dueKey) return { days, dayKey: '', allowed: false, reason: 'Pick a due date first.' };
    const dayKey = reminderDayKey(dueKey, days);
    if (dayKey < todayKey) return { days, dayKey, allowed: false, reason: 'That day has already passed.' };
    if (showOnKey && dayKey < showOnKey) {
      return { days, dayKey, allowed: false, reason: `That is before it shows on ${shortDateLabel(showOnKey, todayKey)}.` };
    }
    if (showOnKey && dayKey === showOnKey) {
      return { days, dayKey, allowed: false, reason: 'That is the day it shows, and showing sends its own alert.' };
    }
    if (dayKey === todayKey) {
      return { days, dayKey, allowed: false, reason: 'That is today, and a new to-do sends its own alert.' };
    }
    return { days, dayKey, allowed: true, reason: null };
  });
}

/**
 * The next studio day a picked reminder still owes, after `afterKey`, on or
 * before the due day; null when none is left. This is what the sweep's daily
 * query looks for (tasks.reminderNextOn), so a to-do costs nothing on a day
 * it has nothing to send.
 */
export function nextReminderOn(input: {
  days: readonly number[];
  dueKey: string;
  sent: Record<string, unknown>;
  afterKey: string;
}): string | null {
  let next: string | null = null;
  for (const days of normalizeReminderDays(input.days)) {
    if (input.sent[String(days)]) continue;
    const dayKey = reminderDayKey(input.dueKey, days);
    if (dayKey <= input.afterKey || dayKey > input.dueKey) continue;
    if (next === null || dayKey < next) next = dayKey;
  }
  return next;
}

/**
 * The picked reminders whose day has come by `todayKey` and that have not
 * been sent, while the due day has not passed. Several can come due at once
 * (a skipped morning); one alert covers them all.
 */
export function remindersDueOn(input: {
  days: readonly number[];
  dueKey: string;
  sent: Record<string, unknown>;
  todayKey: string;
}): TaskReminderDay[] {
  if (input.todayKey > input.dueKey) return [];
  return normalizeReminderDays(input.days).filter((days) => (
    !input.sent[String(days)] && reminderDayKey(input.dueKey, days) <= input.todayKey
  ));
}

/**
 * Is this to-do still waiting for its Show on day? True while its people are
 * held in showOnAssigneeUids. The sweep empties that list the morning it
 * shows, so this is false from then on.
 */
export function isTaskWaitingToShow(task: { showOnAssigneeUids?: unknown } | null | undefined): boolean {
  const list = task?.showOnAssigneeUids;
  return Array.isArray(list) && list.some((uid) => typeof uid === 'string' && uid.length > 0);
}

/** The name a to-do with a Show on day or reminders goes out under. */
export const TASK_STUDIO_NAME = 'The Studio LDG';

/**
 * DOES THE STUDIO LDG SPEAK FOR THIS TO-DO? True for a to-do made with a Show
 * on day or with reminders picked: one still waiting (showOn, its people held
 * in showOnAssigneeUids), one that has shown (the sweep stamps shownOn and
 * keeps it), and one with reminders before its due date (reminderDaysBefore,
 * kept after they go out).
 *
 * Bobbie, DECISIONS 99: the Show on alert and every reminder are "All from
 * The Studio LDG". Then DECISIONS 123 item 3, asked whether the to-do's own
 * maker line ("Posted by", "Ask", a family's "From") should say so too: "The
 * Studio LDG everywhere". So every screen that names who made such a to-do
 * names The Studio LDG instead, for everyone, its maker included, the same
 * on every sign-in. Only the WORDS change: who made it stays on the to-do
 * (createdBy, createdByName). The one exception is Ask, which opens a private
 * message with that person: its button still names her (taskMakerLabel in
 * lib/tasks.ts), because saying the question goes to The Studio LDG would be
 * untrue, and nobody has ruled on a new place for it to go.
 *
 * Every other to-do keeps naming its maker. Firebase-free, so the server (the
 * dancer portal's family to-dos) asks the same question.
 */
export function isStudioVoiceTask(task: {
  showOn?: unknown;
  shownOn?: unknown;
  showOnAssigneeUids?: unknown;
  reminderDaysBefore?: unknown;
} | null | undefined): boolean {
  if (!task) return false;
  return isDateKey(task.showOn)
    || isDateKey(task.shownOn)
    || isTaskWaitingToShow(task)
    || normalizeReminderDays(task.reminderDaysBefore).length > 0;
}
